import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  message,
  Space,
  Modal,
  Form,
  Tooltip,
  Row,
  Tag,
  Empty,
  Select
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  UndoOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons';
import { membershipService, certificationService } from '../../../services';
import dayjs from 'dayjs';
import {ExtendedCertificationResponse} from "../../../model/props/changeFlag";
import {
  CertificationData, CertificationsTabProps,
  MembershipCertificationCreateItem,
  MembershipCertificationDeleteItem,
  MembershipCertificationUpdateItem
} from "../../../model";
import {toROCDate} from "../../../utils/DateUtil";
import RocDateInputPicker from "../../../components/shared/RocDateInputPicker";



const { confirm } = Modal;
const { Option } = Select;



const CertificationsTab: React.FC<CertificationsTabProps> = ({
  membershipId,
  onDataChanged,
  changedFields,
  onSaveSuccess,
  isEditing,
}) => {
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState<ExtendedCertificationResponse[]>([]);
  const [latestSeq, setLatestSeq] = useState<number>(0);
  const [certificationData, setCertificationData] = useState<ExtendedCertificationResponse[]>([]);
  const [modifiedCertifications, setModifiedCertifications] = useState<{[key: number]: boolean}>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [availableCertifications, setAvailableCertifications] = useState<CertificationData[]>([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentCertification, setCurrentCertification] = useState<ExtendedCertificationResponse | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const userAccount = localStorage.getItem('userAccount') ?? '';

  // 載入認證資料
  const fetchCertificationData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await membershipService.getCertifications({ membershipId });
      if (data && Array.isArray(data)) {
        setCertificationData(data);
        setOriginalData(JSON.parse(JSON.stringify(data))); // 深拷貝保存原始資料
          const latestSeq = data.length > 0
              ? Math.max(...data.map(item => item.seq))
              : 0;
          setLatestSeq(latestSeq)
        setModifiedCertifications({});
        setHasChanges(false);
        // 通知父元件已無變更
        onDataChanged('certifications', false);
      } else {
        setCertificationData([]);
        setOriginalData([]);
        message.info('沒有找到認證資料');
      }
    } catch (error) {
      console.error('Failed to fetch certification data:', error);
      message.error('載入認證資料時發生錯誤');
      setCertificationData([]);
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  }, [membershipId, onDataChanged]);

  const fetchAvailableCertifications = useCallback(async () => {
    try {
      // 使用 certificationService 取得可用認證清單
      const certificationData = await certificationService.search({});

      if (certificationData && certificationData.length > 0) {
        // 將 API 回傳的認證資料轉換成我們需要的格式
        const formattedCertifications: CertificationData[] = certificationData.map(cert => ({
          id: cert.certificationId,
          name: cert.certificationName,
          code: cert.certificationCode
        }));

        setAvailableCertifications(formattedCertifications);
      }
    } catch (error) {
      console.error('Failed to fetch available certifications:', error);
      message.error('載入可用認證時發生錯誤');
    }
  }, []);

  useEffect(() => {
    fetchCertificationData();
    fetchAvailableCertifications();
  }, [fetchCertificationData, fetchAvailableCertifications]);

  // 判斷認證是否已修改
  const isCertificationModified = (record: ExtendedCertificationResponse) => {
    return !!modifiedCertifications[record.membershipCertificationId];
  };

  // 認證是否被標記為刪除
  const isCertificationDeleted = (record: ExtendedCertificationResponse) => {
    return !!record._deleted;
  };

  // 認證是否為新增
  const isCertificationNew = (record: ExtendedCertificationResponse) => {
    return record.membershipCertificationId < 0 || !!record._new;
  };

  // 新增認證
  const handleAddCertification = async () => {
    if (!isEditing) return;

    try {
      const values = await addForm.validateFields();

      // 檢查是否已存在相同認證
      const isDuplicate = certificationData.some(item =>
        item.certificationId === values.certificationId && !item._deleted
      );

      if (isDuplicate) {
        message.error('此認證已存在，請選擇其他認證');
        return;
      }

      // 格式化有效日期
      const formattedValidDate = values.validDate ?
        dayjs(values.validDate).format('YYYY-MM-DD') : undefined;

      // 將新認證加入至暫存資料中
      const selectedCertification = availableCertifications.find(c => c.id === values.certificationId);
      if (selectedCertification) {
          console.log(latestSeq);
        const seq = latestSeq + 1;
        setLatestSeq(seq);
        const newCertification: ExtendedCertificationResponse = {
          membershipCertificationId: -Date.now(), // 臨時负值 ID，用來識別新增項目
          certificationId: selectedCertification.id,
          certificationCode: selectedCertification.code,
          certificationName: selectedCertification.name,
          seq: seq,
          validDate: formattedValidDate,
          _new: true
        };

        const newData = [...certificationData, newCertification];
        setCertificationData(newData);

        // 標記為已修改
        setModifiedCertifications(prev => ({
          ...prev,
          [newCertification.membershipCertificationId]: true
        }));
        setHasChanges(true);

        // 通知上層組件有資料變更
        onDataChanged('certifications', true);

        // setIsAddModalVisible(false);
        addForm.resetFields();
        message.success('已添加認證，點擊儲存按鈕以儲存變更');
      }
    } catch (error) {
      console.error('Add certification failed:', error);
      message.error('新增認證時發生錯誤');
    }
  };

  // 更新認證
  const handleUpdateCertification = async () => {
    if (!currentCertification || !isEditing) return;

    try {
      const values = await editForm.validateFields();

      // 檢查是否與其他認證重複
      const isDuplicate = certificationData.some(item =>
        item.certificationId === values.certificationId &&
        item.membershipCertificationId !== currentCertification.membershipCertificationId &&
        !item._deleted
      );

      if (isDuplicate) {
        message.error('此認證已存在，請選擇其他認證');
        return;
      }

      // 格式化有效日期
      const formattedValidDate = values.validDate ?
        dayjs(values.validDate).format('YYYY-MM-DD') : undefined;

      // 在暫存資料中更新產品
      const selectedCertification = availableCertifications.find(c => c.id === values.certificationId);
      if (selectedCertification) {
        const newData = certificationData.map(item => {
          if (item.membershipCertificationId === currentCertification.membershipCertificationId) {
            return {
              ...item,
              certificationId: selectedCertification.id,
              certificationCode: selectedCertification.code,
              certificationName: selectedCertification.name,
              validDate: formattedValidDate
            };
          }
          return item;
        });

        setCertificationData(newData);

        // 標記為已修改
        setModifiedCertifications(prev => ({
          ...prev,
          [currentCertification.membershipCertificationId]: true
        }));
        setHasChanges(true);

        // 通知上層組件有資料變更
        onDataChanged('certifications', true);

        setIsEditModalVisible(false);
        setCurrentCertification(null);
        editForm.resetFields();
        message.success('已更新認證，點擊儲存按鈕以儲存變更');
      }
    } catch (error) {
      console.error('Update certification failed:', error);
      message.error('更新認證時發生錯誤');
    }
  };

  // 刪除認證
  const handleDeleteCertification = (record: ExtendedCertificationResponse) => {
    if (!isEditing) return;

    // 如果是新增的項目（负數 ID）
    if (record.membershipCertificationId < 0) {
      // 直接從暫存資料中移除
      setCertificationData(certificationData.filter(item =>
        item.membershipCertificationId !== record.membershipCertificationId
      ));

      // 從修改記錄中移除
      const updatedModified = { ...modifiedCertifications };
      delete updatedModified[record.membershipCertificationId];
      setModifiedCertifications(updatedModified);

      // 更新變更狀態
      const hasRemainingChanges = Object.keys(updatedModified).length > 0;
      setHasChanges(hasRemainingChanges);
      onDataChanged('certifications', hasRemainingChanges);

      message.success('已移除認證');
      return;
    }

    // 將現有認證標記為待刪除
    setCertificationData(prevData =>
      prevData.map(item =>
        item.membershipCertificationId === record.membershipCertificationId
          ? { ...item, _deleted: true }
          : item
      )
    );

    // 標記為已修改
    setModifiedCertifications(prev => ({
      ...prev,
      [record.membershipCertificationId]: true
    }));
    setHasChanges(true);

    // 通知上層組件有資料變更
    onDataChanged('certifications', true);

    message.success('已標記認證為刪除，點擊儲存按鈕以完成刪除');
  };

  // 確認刪除認證
  const confirmDelete = (record: ExtendedCertificationResponse) => {
    if (!isEditing) return;

    confirm({
      title: '確定要刪除此認證嗎？',
      icon: <ExclamationCircleFilled />,
      content: `將會移除認證：${record.certificationName} (${record.certificationCode})`,
      okText: '確定',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        handleDeleteCertification(record);
      },
    });
  };

  // 開啟編輯視窗
  const showEditModal = (record: ExtendedCertificationResponse) => {
    if (!isEditing) return;

    // 將日期字串轉為 dayjs 對象
    const validDate = record.validDate ? dayjs(record.validDate) : null;

    setCurrentCertification(record);
    editForm.setFieldsValue({
      certificationId: record.certificationId,
      validDate: validDate
    });
    setIsEditModalVisible(true);
  };

  // 儲存所有變更
  const handleSaveChanges = async () => {
    if (!hasChanges || !isEditing) {
      message.info('沒有需要儲存的變更');
      return;
    }

    setLoading(true);

    try {
      // 備份原本資料，用於失敗時還原
      const originalCertificationDataBackup = JSON.parse(JSON.stringify(certificationData));

      // 准備要新增的認證
      const certificationsToAdd = certificationData
        .filter(c => c.membershipCertificationId < 0 || c._new) // 新增的認證有臨時负值ID或_new標記
        .map(c => ({
          membershipId,
          certificationId: c.certificationId,
          validDate: c.validDate,
          seq: c.seq,
          createdBy: userAccount
        }) as MembershipCertificationCreateItem);

      // 准備要更新的認證
      const certificationsToUpdate = certificationData
        .filter(c => c.membershipCertificationId > 0 && // 正常 ID
                  modifiedCertifications[c.membershipCertificationId] && // 已修改
                  !c._deleted && // 不是待刪除
                  !c._new) // 不是新增項目
        .map(c => ({
          membershipCertificationId: c.membershipCertificationId,
          membershipId,
          certificationId: c.certificationId,
          validDate: c.validDate,
          seq: c.seq,
          modifiedBy: userAccount
        }) as MembershipCertificationUpdateItem);

      // 准備要刪除的認證
      const certificationsToDelete = certificationData
        .filter(c => c.membershipCertificationId > 0 && c._deleted) // 被標記為刪除的現有認證
        .map(c => ({
          membershipCertificationId: c.membershipCertificationId
        }) as MembershipCertificationDeleteItem);

      // 执行 API 請求
      let isSuccess = true;
      let errorMsg = '';

      // 新增認證
      if (certificationsToAdd.length > 0) {
        try {
          const createResponse = await membershipService.createCertifications({
            membershipCertifications: certificationsToAdd
          });

          if (!createResponse || createResponse.createdCount === 0) {
            isSuccess = false;
            errorMsg = '新增認證失敗';
          }
        } catch (error) {
          console.error('Create certifications failed:', error);
          isSuccess = false;
          errorMsg = '新增認證時發生錯誤';
        }
      }

      // 更新認證
      if (isSuccess && certificationsToUpdate.length > 0) {
        try {
          const updateResponse = await membershipService.updateCertifications({
            membershipCertifications: certificationsToUpdate
          });

          if (!updateResponse || updateResponse.updatedCount === 0) {
            isSuccess = false;
            errorMsg = '更新認證失敗';
          }
        } catch (error) {
          console.error('Update certifications failed:', error);
          isSuccess = false;
          errorMsg = '更新認證時發生錯誤';
        }
      }

      // 刪除認證
      if (isSuccess && certificationsToDelete.length > 0) {
        try {
          const deleteResponse = await membershipService.deleteCertifications({
            membershipCertificationIds: certificationsToDelete,
            modifiedBy: userAccount
          });

          if (!deleteResponse || deleteResponse.deletedCount === 0) {
            isSuccess = false;
            errorMsg = '刪除認證失敗';
          }
        } catch (error) {
          console.error('Delete certifications failed:', error);
          isSuccess = false;
          errorMsg = '刪除認證時發生錯誤';
        }
      }

      if (isSuccess) {
        message.success('所有認證變更已儲存成功');
        // 重新載入最新資料
        fetchCertificationData();
        onSaveSuccess();
      } else {
        // 還原原始資料
        setCertificationData(originalCertificationDataBackup);
        message.error(errorMsg || '儲存認證變更失敗');
      }
    } catch (error) {
      console.error('Save certifications failed:', error);
      message.error('儲存認證變更時發生錯誤');
      // 還原原始資料
      handleResetChanges();
    } finally {
      setLoading(false);
    }
  };

  // 重置變更
  const handleResetChanges = () => {
    setCertificationData(JSON.parse(JSON.stringify(originalData)));
    setModifiedCertifications({});
    setHasChanges(false);
    onDataChanged('certifications', false);
    message.info('已返回原始資料');
  };

  // 獲取表格欄位定義
  const getColumns = () => {
    // 基本欄位
    const baseColumns = [
      {
        title: '認證代號',
        dataIndex: 'certificationCode',
        key: 'certificationCode',
        render: (text: string, record: ExtendedCertificationResponse) => {
          if (isCertificationDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text}</span>;
          }
          return isCertificationModified(record) ?
            <span style={{ color: '#f50' }}>{text}</span> :
            text;
        }
      },
      {
        title: '認證名稱',
        dataIndex: 'certificationName',
        key: 'certificationName',
        render: (text: string, record: ExtendedCertificationResponse) => {
          if (isCertificationDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text}</span>;
          }
          return isCertificationModified(record) ?
            <span style={{ color: '#f50' }}>{text}</span> :
            text;
        }
      },
      {
        title: '有效日期',
        dataIndex: 'validDate',
        key: 'validDate',
        render: (text: string, record: ExtendedCertificationResponse) => {
          const displayText = text || '-';
          if (isCertificationDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{toROCDate(displayText)}</span>;
          }
          return isCertificationModified(record) ?
            <span style={{ color: '#f50' }}>{toROCDate(displayText)}</span> :
              toROCDate(displayText);
        }
      }
    ];

    // 只在編輯模式下添加額外的欄位
    if (isEditing) {
      return [
        ...baseColumns,
        {
          title: '狀態',
          key: 'status',
          width: 100,
          render: (_: any, record: ExtendedCertificationResponse) => {
            if (isCertificationDeleted(record)) {
              return <Tag color="red">待刪除</Tag>;
            }
            if (isCertificationNew(record)) {
              return <Tag color="green">新增</Tag>;
            }
            if (isCertificationModified(record)) {
              return <Tag color="orange">已修改</Tag>;
            }
            return null;
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          render: (_: any, record: ExtendedCertificationResponse) => {
            if (isCertificationDeleted(record)) {
              return (
                <Button
                  type="link"
                  onClick={() => {
                    // 取消刪除標記
                    setCertificationData(prevData =>
                      prevData.map(item =>
                        item.membershipCertificationId === record.membershipCertificationId
                          ? { ...item, _deleted: false }
                          : item
                      )
                    );
                    // 使用時間戳創建新的修改記錄，以確保觸發狀態更新
                    setModifiedCertifications(prev => {
                      const newModified = { ...prev };
                      delete newModified[record.membershipCertificationId];
                      return newModified;
                    });
                    // 更新變更狀態
                    setHasChanges(true);
                    onDataChanged('certifications', true);
                  }}
                >
                  取消刪除
                </Button>
              );
            }

            return (
              <Space size="small">
                <Tooltip title="編輯">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => showEditModal(record)}
                    disabled={isCertificationDeleted(record)}
                  />
                </Tooltip>
                <Tooltip title="刪除">
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    danger
                    onClick={() => confirmDelete(record)}
                    disabled={isCertificationDeleted(record)}
                  />
                </Tooltip>
              </Space>
            );
          },
        }
      ];
    }

    return baseColumns;
  };

  const renderTable = () => {
    if (!certificationData || certificationData.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="尚無認證資料"
        />
      );
    }

    return (
      <Table
        sticky
        rowKey="membershipCertificationId"
        columns={getColumns()}
        dataSource={certificationData.filter(item => !isCertificationDeleted(item) || isEditing)}
        loading={loading}
        pagination={false}
        rowClassName={(record) => isCertificationModified(record) ? 'modified-row' : ''}
      />
    );
  };

  return (
    <Card>
      {isEditing && (
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddModalVisible(true)}
            >
              新增認證
            </Button>
          </Space>

          <Space>
            <Button
              icon={<UndoOutlined />}
              onClick={handleResetChanges}
              disabled={!hasChanges}
            >
              重置
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveChanges}
              loading={loading}
              disabled={!hasChanges}
            >
              儲存變更
            </Button>
          </Space>
        </Row>
      )}

      {renderTable()}

      {/* 新增認證視窗 */}
      <Modal
        title="新增認證"
        open={isAddModalVisible}
        onOk={handleAddCertification}
        onCancel={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
        }}
        okText="新增"
        cancelText="關閉"
        confirmLoading={loading}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="certificationId"
            label="選擇認證"
            rules={[{ required: true, message: '請選擇認證' }]}
          >
            <Select
              placeholder="請選擇認證"
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {availableCertifications.map(cert => (
                <Option key={cert.id} value={cert.id}>
                  {cert.name} ({cert.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="validDate"
            label="有效日期"
          >
            {/*<DatePicker */}
            {/*  placeholder="請選擇有效日期（選填）" */}
            {/*  format="YYYY-MM-DD"*/}
            {/*  locale={locale}*/}
            {/*  style={{ width: '100%' }}*/}
            {/*/>*/}
            {/*  <ROCDatePicker*/}
            {/*      placeholder="請選擇有效日期（選填）"*/}
            {/*      style={{width: '100%'}}*/}
            {/*  />*/}
              <RocDateInputPicker />
          </Form.Item>
        </Form>
      </Modal>

      {/* 編輯認證視窗 */}
      <Modal
        title="編輯認證"
        open={isEditModalVisible}
        onOk={handleUpdateCertification}
        onCancel={() => {
          setIsEditModalVisible(false);
          setCurrentCertification(null);
          editForm.resetFields();
        }}
        okText="更新"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="certificationId"
            label="選擇認證"
            rules={[{ required: true, message: '請選擇認證' }]}
          >
            <Select
              placeholder="請選擇認證"
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {availableCertifications.map(cert => (
                <Option key={cert.id} value={cert.id}>
                  {cert.name} ({cert.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="validDate"
            label="有效日期"
          >
              <RocDateInputPicker />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CertificationsTab;
